import React, { useContext, useEffect, useState } from "react";
import { Button, Container, Form, Modal } from "react-bootstrap";
import ErrorDisplay from "../common/ErrorDisplay";
import { Problem } from "../api/backendApi";
import { drawingApi, LayerModel } from "../api/drawingApi";
import { DrawingContext } from "./DrawingContext";

interface LayerEditModalProps {
  parentName: string;
  name: string | undefined;
  readOnly: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
}

function LayerEditModal({
  parentName,
  name,
  readOnly: initialReadOnly,
  onClose,
  onSave,
}: LayerEditModalProps) {
  const drawing = useContext(DrawingContext);
  const [error, setError] = useState<string | Problem | undefined>();
  const [layer, setLayer] = useState<LayerModel>({});
  const [readOnly, setReadOnly] = useState<boolean>(true);

  useEffect(() => {
    async function load() {
      if (name) {
        const response = await drawingApi.getLayer(
          drawing.name,
          drawing.type,
          name,
        );

        if (response.ok) {
          setError(undefined);
          setLayer(response.value);
          setReadOnly(initialReadOnly);
        } else {
          setError(response.problem);
        }
      } else {
        setReadOnly(initialReadOnly);
      }
    }

    load();
  }, [drawing.name, drawing.type, initialReadOnly, name]);

  const handleClose = () => {
    setError(undefined);
    onClose();
  };

  const handleSave = async () => {
    if (!layer.name || layer.name.length === 0) {
      setError("A name is required.");
      return;
    }

    const result = await drawingApi.upsertLayer(
      drawing.name,
      drawing.type,
      parentName,
      name,
      layer,
    );

    if (result.ok) {
      setError(undefined);

      onSave(layer.name);

      onClose();
    } else {
      setError(result.problem);
    }
  };

  return (
    <Modal show={true} onHide={() => handleClose()}>
      <Modal.Header>
        <Modal.Title>{name ?? "New Layer"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container>
          <ErrorDisplay error={error} />
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control
              defaultValue={name}
              readOnly={readOnly}
              onChange={(e) => setLayer({ ...layer, name: e.target.value })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>CSS Class Name</Form.Label>
            <Form.Control
              defaultValue={layer.cssClassName}
              readOnly={readOnly}
              onChange={(e) =>
                setLayer({ ...layer, cssClassName: e.target.value })
              }
            />
          </Form.Group>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => handleClose()}>Cancel</Button>
        <Button disabled={readOnly} onClick={() => handleSave()}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default LayerEditModal;
